import { FC, useState, useEffect, useRef, useMemo, } from "react";
// 组件
import DropdownTable from '../DropdownTable';
// 常量
import { DropdownSyncTableProps } from "./interface";
import { DEFAULT_FIELD_NAMES, DropdownTableOption, DropdownTableValue, } from "../DropdownTable/interface";
import { API_STATUS_ENUM } from "../constants/enum";
import { SUCCESS_STATUS_CODE } from "../constants/constant";
import { PaginationProps } from "antd/lib/pagination";
import { TableActionType } from "../SuperTable/interface";

/** 异步下拉表格 待完善 */
const DropdownSyncTable: FC<DropdownSyncTableProps> = props => {
  const {
    value: outValue, pagination: outPagination, fieldNames, otherParams, isTableList, dataKey, cacheKey,
    isOpenUpdate, initOptions, defaultSelectedFirstItem, mode, isLocalSearch, searchKey, initialLoading,
    fetchApi, onBefore, filterData, onBlur, onFocus, onClear, onChange, onSearch, onSuccess, ...restProps
  } = props;
  const [ loading, setLoading, ] = useState<boolean>(false);
  const [ value, setValue, ] = useState<DropdownTableValue>();
  const [ pagination, setPagination, ] = useState<PaginationProps | false | undefined>(outPagination);
  const [ options, setOptions, ] = useState<DropdownTableOption[]>();
  const tableRef = useRef<TableActionType>(); // 选项表格
  const apiStatus = useRef<API_STATUS_ENUM>(API_STATUS_ENUM.NOT_START); // 接口状态
  const timer = useRef<any>(); // 延时过滤选项定时器
  const prevBeforeFetchParams = useRef<any>(); // 上一次请求的参数
  const selectedOptions = useRef<DropdownTableOption | DropdownTableOption[]>(); // 选中数据
  const fetchNumberRef = useRef<number>(0); // 当前请求标识
  const currentSearchValue = useRef<string>(); // 搜索内容
  useEffect(() => {
    if (initialLoading) {
      handleFetch();
    }
  }, []);
  useEffect(() => {
    const outValueType: boolean = Array.isArray(outValue);
    if (mode && !outValueType) {
      setValue(undefined);
      return;
    }
    if (!mode && outValueType) {
      setValue(undefined);
      return;
    }
    setValue(outValue);
  }, [outValue]);
  useEffect(() => {
    if (Array.isArray(initOptions) && initOptions.length && apiStatus.current !== API_STATUS_ENUM.SUCCESS) {
      setOptions(initOptions);
    }
  }, [initOptions]);
  /** 获取数据 */
  const handleFetch = async (paginationParams: any = pagination, searchValue?: string) => {
    if (!fetchApi) return;
    let newParams: any = { ...otherParams };
    const searchName = searchKey || fieldNames?.label || DEFAULT_FIELD_NAMES.label;
    if (searchValue && searchName) {
      newParams[searchName] = searchValue;
    }
    if (onBefore) {
      newParams = onBefore(newParams);
      if (newParams === false) {
        apiStatus.current = API_STATUS_ENUM.ERROR;
        setOptions(undefined);
        return;
      }
    }
    if (isTableList) {
      const searcher = { ...newParams, ...paginationParams, };
      const page = searcher.page || 1;
      const pageSize = searcher.pageSize || 20;
      delete searcher.page;
      delete searcher.pageSize;
      newParams = {
        page,
        pageSize,
        searcher,
      };
    }
    // 是否需要从缓存中读取
    if (cacheKey && (!isTableList || (isTableList && newParams.page === 1))) {
      const CACHE_OPTIONS = window.sessionStorage.getItem(cacheKey);
      if (CACHE_OPTIONS) {
        if (CACHE_OPTIONS === API_STATUS_ENUM.LOADING) {
          timer.current && clearInterval(timer.current);
          timer.current = setInterval(() => {
            const resData = window.sessionStorage.getItem(cacheKey);
            if (resData && resData !== API_STATUS_ENUM.LOADING) {
              clearInterval(timer.current);
              const data = JSON.parse(resData);
              handleDataProcessing(data);
            }
          }, 100);
        } else {
          const data = JSON.parse(CACHE_OPTIONS);
          handleDataProcessing(data);
        }
        return;
      } else {
        window.sessionStorage.setItem(cacheKey, API_STATUS_ENUM.LOADING);
      }
    }
    // 比对两次请求参数是否相同，相同这不在发起请求
    if (!isOpenUpdate) {
      if (prevBeforeFetchParams.current) {
        const prevStringify = JSON.stringify(prevBeforeFetchParams.current);
        const nextStringify = JSON.stringify(newParams);
        if (prevStringify === nextStringify) return;
        prevBeforeFetchParams.current = newParams;
        selectedOptions.current = undefined;
      } else {
        prevBeforeFetchParams.current = newParams;
      }
    }
    const currentNumebr = fetchNumberRef.current + 1;
    fetchNumberRef.current = currentNumebr;
    apiStatus.current = API_STATUS_ENUM.LOADING;
    setLoading(true);
    const res = await fetchApi(newParams);
    setLoading(false);
    if (res.code === SUCCESS_STATUS_CODE && currentNumebr === fetchNumberRef.current) {
      apiStatus.current = API_STATUS_ENUM.SUCCESS;
      const data = (dataKey ? res.data?.[dataKey] : res.data) || [];
      if (isTableList) {
        setPagination(prev => ({ ...prev, total: res.data?.total, }));
      } else if (pagination) {
        setPagination(prev => ({ ...prev, total: data?.length, }));
      }
      // 数据存入缓存
      if (cacheKey) {
        window.sessionStorage.setItem(
          cacheKey,
          JSON.stringify(data || []),
        );
      }
      handleDataProcessing(data);
      return;
    }
    apiStatus.current = API_STATUS_ENUM.ERROR;
  };
  /** 远程获取的数据处理 */
  const handleDataProcessing = (data: any = []) => {
    const result: any[] = filterData ? filterData(data) : data;
    const selectedRows = selectedOptions.current;
    const currentValueKey = fieldNames?.value || DEFAULT_FIELD_NAMES.value;
    let otherOptions: DropdownTableOption[] = [];
    if (selectedRows) {
      if (Array.isArray(selectedRows)) {
        otherOptions.push(...selectedRows);
      } else {
        otherOptions.push(selectedRows);
      }
    }
    if (value && initOptions) otherOptions.push(...initOptions);
    if (otherOptions.length) {
      otherOptions.forEach(item => {
        const findOption = result.find(v => v[currentValueKey] === item[currentValueKey]);
        if (!findOption) {
          result.unshift(item);
        }
      });
    }
    setOptions(result);
    if (Array.isArray(result) && result.length && !value && defaultSelectedFirstItem) {
      const firstValue = result[0][currentValueKey];
      if (mode === 'multiple') {
        setValue([firstValue]);
        onChange?.([firstValue], [result[0]]);
      } else {
        setValue(firstValue);
        onChange?.(firstValue, result[0]);
      }
      onSuccess?.(result);
      return;
    }
  };
  /** 下拉列表获取焦点 */
  const handleFocus = async () => {
    if (isOpenUpdate || apiStatus.current === API_STATUS_ENUM.NOT_START) {
      await handleFetch();
    }
    onFocus?.();
  };
  /** 搜索 */
  const handleSearch = async (searchValue?: string) => {
    if (isLocalSearch) return;
    timer.current && clearTimeout(timer.current);
    loading && clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      currentSearchValue.current = searchValue;
      handleFetch({ page: 1, }, searchValue);
    }, 300);
  }
  /** value值改变 */
  const handleChange = (val?: DropdownTableValue, options?: DropdownTableOption | DropdownTableOption[]) => {
    selectedOptions.current = options;
    setValue(val);
    onChange?.(val, options);
  };
  /** 分页数据改变 */
  const handlePaginationChange = (pageParams: any) => {
    const { current: page, pageSize, } = pageParams;
    setPagination(prev => ({ ...prev, ...pageParams, }));
    handleFetch({ page, pageSize, }, currentSearchValue.current);
  };
  return (
    <DropdownTable
      {...restProps}
      tableRef={tableRef}
      value={value}
      mode={mode}
      fieldNames={fieldNames}
      loading={loading}
      options={options}
      pagination={pagination}
      onFocus={handleFocus}
      onBlur={onBlur}
      onSearch={handleSearch}
      onChange={handleChange}
      onPaginationChange={handlePaginationChange}
    />
  );
};

DropdownSyncTable.defaultProps = {
  dataKey: false,
  isTableList: false,
  initialLoading: true,
  pagination: false,
  placeholder: '请选择',
  allowClear: true,
  isLocalSearch: true,
  autoPage: true,
  defaultSelectedFirstItem: false,
  isOpenUpdate: false,
};

export default DropdownSyncTable;
